import { useCallback, useEffect, useRef, useState } from 'react';
import { useEstaOnline } from '@/features/auth/AuthContext';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { Toast } from '@/shared/ui/Toast';
import { capturaApi, mensagemErroCaptura } from '@/features/captura/api/capturaApi';
import { useSessaoCaptura } from '@/features/captura/hooks/useSessaoCaptura';
import { tentarVinculosPendentes } from '@/features/captura/vinculosPendentes';
import type { FotoCapturaDTO } from '@/features/captura/tipos';
import { ModalCapturaCelular } from '@/features/captura/components/ModalCapturaCelular';
import {
  IconCamera,
  IconLixeira,
  IconSetaEsquerda,
} from '@/features/captura/components/icones';
import '@/features/captura/captura.css';

type Props = {
  orcamentoId: string;
  /** Nº da O.S. (cabeçalho da página do celular). */
  numero?: string | null;
  placa?: string | null;
  titulo?: string;
  /** Avisa a tela sempre que a lista muda (ex.: para a impressão A4). */
  aoMudarFotos?: (fotos: FotoCapturaDTO[]) => void;
};

const dataFmt = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

function formatarData(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : dataFmt.format(d);
}

function ordenar(lista: FotoCapturaDTO[]): FotoCapturaDTO[] {
  return [...lista].sort((a, b) => a.criadoEm.localeCompare(b.criadoEm));
}

function mesclar(lista: FotoCapturaDTO[], foto: FotoCapturaDTO): FotoCapturaDTO[] {
  return lista.some((f) => f.id === foto.id)
    ? lista.map((f) => (f.id === foto.id ? foto : f))
    : [...lista, foto];
}

/**
 * Fotos do veículo vinculadas à O.S. — grade com miniaturas, lightbox,
 * remoção com confirmação e o botão que abre o QR para fotografar pelo
 * celular. Fotos novas chegam ao vivo enquanto o QR está aberto.
 */
export function GaleriaFotosOS({
  orcamentoId,
  numero,
  placa,
  titulo = 'Fotos do veículo',
  aoMudarFotos,
}: Props) {
  const online = useEstaOnline();
  const dialogos = useDialogos();
  const [fotos, setFotos] = useState<FotoCapturaDTO[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [carregou, setCarregou] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [qrAberto, setQrAberto] = useState(false);
  const [indice, setIndice] = useState<number | null>(null);
  const [removendoId, setRemovendoId] = useState<string | null>(null);
  const pedidoRef = useRef(0);

  const captura = useSessaoCaptura({
    aoReceberFoto: (foto) => setFotos((lista) => mesclar(lista, foto)),
    aoRemoverFoto: (id) => setFotos((lista) => lista.filter((f) => f.id !== id)),
  });

  const aoMudarFotosRef = useRef(aoMudarFotos);
  useEffect(() => {
    aoMudarFotosRef.current = aoMudarFotos;
  });
  useEffect(() => {
    aoMudarFotosRef.current?.(fotos);
  }, [fotos]);

  const carregar = useCallback(async () => {
    if (!online) return;
    const pedido = ++pedidoRef.current;
    setCarregando(true);
    setErro(null);
    try {
      // Fotos de uma nova O.S. cujo vínculo falhou ao salvar entram agora.
      await tentarVinculosPendentes(orcamentoId).catch(() => 0);
      const lista = await capturaApi.listarFotos({ orcamentoId });
      if (pedido !== pedidoRef.current) return;
      setFotos(ordenar(Array.isArray(lista) ? lista : []));
      setCarregou(true);
    } catch (e) {
      if (pedido !== pedidoRef.current) return;
      setErro(mensagemErroCaptura(e, 'Não foi possível carregar as fotos.'));
    } finally {
      if (pedido === pedidoRef.current) setCarregando(false);
    }
  }, [orcamentoId, online]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const fecharToast = useCallback(() => setToast(null), []);

  function fecharQr() {
    setQrAberto(false);
    // O que chegou pelo socket já está na tela; recarrega para pegar
    // remoções/edições feitas no celular enquanto o socket oscilava.
    void carregar();
  }

  async function remover(foto: FotoCapturaDTO) {
    const ok = await dialogos.confirmar({
      titulo: 'Remover foto?',
      mensagem: `A foto "${foto.areaRotulo}" será apagada desta ordem de serviço. Não dá para desfazer.`,
      textoConfirmar: 'Remover',
      perigoso: true,
    });
    if (!ok) return;
    setRemovendoId(foto.id);
    try {
      await capturaApi.removerFoto(foto.id);
      setFotos((lista) => lista.filter((f) => f.id !== foto.id));
    } catch (e) {
      setToast(mensagemErroCaptura(e, 'Não foi possível remover a foto.'));
    } finally {
      setRemovendoId(null);
    }
  }

  const fotoAberta = indice !== null ? fotos[indice] ?? null : null;

  // Foto aberta saiu da lista (removida aqui ou no celular): mostra a
  // vizinha, ou fecha o lightbox se não sobrou nenhuma.
  useEffect(() => {
    if (indice === null || indice < fotos.length) return;
    setIndice(fotos.length > 0 ? fotos.length - 1 : null);
  }, [indice, fotos.length]);

  // Setas do teclado navegam no lightbox.
  useEffect(() => {
    if (indice === null) return;
    const total = fotos.length;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setIndice((i) => (i !== null && i > 0 ? i - 1 : i));
      if (e.key === 'ArrowRight') setIndice((i) => (i !== null && i < total - 1 ? i + 1 : i));
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [indice, fotos.length]);

  const botaoTirar = (
    <Button
      onClick={() => setQrAberto(true)}
      disabled={!online}
      title={!online ? 'Requer internet' : undefined}
    >
      <IconCamera />
      Tirar fotos pelo celular
    </Button>
  );

  return (
    <div className="card">
      <div className="card__header captura-card__header">
        <span className="card__title captura-card__titulo">
          {titulo}
          {fotos.length > 0 && (
            <span className="captura-contador" aria-label={`${fotos.length} fotos`}>
              {fotos.length}
            </span>
          )}
        </span>
        {(fotos.length > 0 || erro || !online) && botaoTirar}
      </div>
      <div className="card__body">
        {!online && (
          <div className="info-banner info-banner--alerta captura-aviso" role="status">
            Sem internet. As fotos ficam no servidor e voltam a aparecer quando a conexão voltar.
          </div>
        )}

        {erro ? (
          <div className="alert alert--error captura-qr__erro" role="alert">
            <span>{erro}</span>
            <Button onClick={() => void carregar()} disabled={carregando || !online}>
              {carregando ? 'Carregando…' : 'Tentar de novo'}
            </Button>
          </div>
        ) : carregando && !carregou ? (
          <ul className="captura-galeria" aria-busy="true" aria-label="Carregando fotos…">
            {[0, 1, 2].map((i) => (
              <li key={i} className="captura-galeria__esqueleto" />
            ))}
          </ul>
        ) : fotos.length === 0 ? (
          online || carregou ? (
            <div className="captura-vazio">
              <span className="captura-vazio__icone">
                <IconCamera />
              </span>
              <p>Nenhuma foto ainda. Use o celular para fotografar o veículo na entrada.</p>
              {botaoTirar}
            </div>
          ) : null
        ) : (
          <ul className="captura-galeria">
            {fotos.map((foto, i) => (
              <li key={foto.id} className="captura-galeria__item">
                <button
                  type="button"
                  className="captura-galeria__miniatura"
                  onClick={() => setIndice(i)}
                  aria-label={`Ampliar foto: ${foto.areaRotulo}`}
                >
                  <img src={foto.url} alt={foto.areaRotulo} loading="lazy" decoding="async" />
                </button>
                <button
                  type="button"
                  className="captura-galeria__remover"
                  onClick={() => void remover(foto)}
                  disabled={removendoId === foto.id}
                  aria-label={`Remover foto: ${foto.areaRotulo}`}
                  data-tooltip-id="icon-tip"
                  data-tooltip-content="Remover foto"
                >
                  <IconLixeira />
                </button>
                <span className="captura-galeria__legenda">
                  <span className="captura-galeria__area">{foto.areaRotulo}</span>
                  <span className="captura-galeria__data">{formatarData(foto.criadoEm)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ModalCapturaCelular
        aberto={qrAberto}
        modo="fotos"
        captura={captura}
        parametros={{ orcamentoId, numero: numero ?? null, placa: placa ?? null }}
        onFechar={fecharQr}
      />

      {fotoAberta && indice !== null && (
        <Modal
          aberto
          tamanho="lg"
          titulo={fotoAberta.areaRotulo}
          onFechar={() => setIndice(null)}
          rodape={
            <>
              <Button onClick={() => setIndice(null)}>Fechar</Button>
              <Button
                className="btn--danger"
                onClick={() => void remover(fotoAberta)}
                disabled={removendoId === fotoAberta.id}
              >
                <IconLixeira />
                {removendoId === fotoAberta.id ? 'Removendo…' : 'Remover foto'}
              </Button>
            </>
          }
        >
          <div className="captura-lightbox">
            <div className="captura-lightbox__palco">
              <img src={fotoAberta.url} alt={fotoAberta.areaRotulo} />
              {fotos.length > 1 && (
                <>
                  <button
                    type="button"
                    className="captura-lightbox__seta captura-lightbox__seta--anterior"
                    onClick={() => setIndice(indice - 1)}
                    disabled={indice === 0}
                    aria-label="Foto anterior"
                  >
                    <IconSetaEsquerda />
                  </button>
                  <button
                    type="button"
                    className="captura-lightbox__seta captura-lightbox__seta--proxima"
                    onClick={() => setIndice(indice + 1)}
                    disabled={indice === fotos.length - 1}
                    aria-label="Próxima foto"
                  >
                    <IconSetaEsquerda />
                  </button>
                </>
              )}
            </div>
            <div className="captura-lightbox__info">
              <strong>{fotoAberta.areaRotulo}</strong>
              <span>
                {indice + 1} de {fotos.length} · {formatarData(fotoAberta.criadoEm)}
              </span>
            </div>
            {fotoAberta.legenda && (
              <p className="captura-lightbox__legenda">{fotoAberta.legenda}</p>
            )}
          </div>
        </Modal>
      )}

      <Toast mensagem={toast} tipo="erro" onFechar={fecharToast} />
    </div>
  );
}
